import { api } from '@/lib/api/client';
import { createTeamApi, getTeamApi, getTeamsApi } from '@api-client/teams';
import type { TeamRegistrationStatus } from '@shared/enums/domain.enum';

export { getTeamsApi, getTeamApi, createTeamApi };
export type { CreateTeamPayload, GetTeamsParams, Team } from '@api-client/teams';

/**
 * Champs mutables d'une équipe (matrice ownership §1.4).
 *
 * - `name` / `notes` / `captainPhone` uniquement.
 * - `registrationStatus` n'est PAS modifiable ici : utiliser
 *   `updateTeamRegistrationStatusApi` (endpoint dédié, réservé ORG_ADMIN).
 * - Les champs immuables (`competitionId`, `clubId`, `captainId`) sont rejetés
 *   par la `ValidationPipe` (`forbidNonWhitelisted`).
 */
export interface UpdateTeamPayload {
  name?: string;
  notes?: string | null;
  captainPhone?: string | null;
}

export async function updateTeamApi(
  id: number,
  data: UpdateTeamPayload,
): Promise<import('@shared/types/entities').Team> {
  return api.patch(`/teams/${id}`, data);
}

/**
 * Met à jour le statut d'inscription via l'endpoint dédié
 * `PATCH /teams/:id/registration-status`.
 *
 * Réservé aux ORG_ADMIN du concours (et SUPER_ADMIN).
 * Workflow : `PENDING → CONFIRMED → REJECTED → CANCELLED`.
 */
export async function updateTeamRegistrationStatusApi(
  id: number,
  status: TeamRegistrationStatus,
): Promise<import('@shared/types/entities').Team> {
  return api.patch(`/teams/${id}/registration-status`, { registrationStatus: status });
}

/**
 * Désactive une équipe (soft cancel) : `registrationStatus = CANCELLED` +
 * décrémente `competition.currentTeams` pour libérer la place.
 *
 * Idempotent. Réservé ORG_ADMIN du concours + SUPER_ADMIN.
 */
export async function disableTeamApi(id: number): Promise<{ message: string; refundRequestCreated: boolean }> {
  return api.delete(`/teams/${id}`);
}

/** @deprecated Utiliser `disableTeamApi`. Alias conservé pour compat éphémère. */
export const deleteTeamApi = disableTeamApi;

/** Récupère toutes les équipes correspondant aux params en paginant automatiquement (limit max = 100). */
export async function fetchAllTeams(
  params: Omit<import('@shared/types/entities').GetTeamsParams, 'page' | 'limit'>,
): Promise<import('@shared/types/entities').Team[]> {
  const limit = 100;
  let page = 1;
  const all: import('@shared/types/entities').Team[] = [];
  while (true) {
    const res = await getTeamsApi({ ...params, limit, page });
    all.push(...res.data);
    if (page >= res.meta.totalPages) break;
    page++;
  }
  return all;
}

/** GV-02: dedicated organizer registration (auth + association scope enforced server-side). */
export async function createOrganizerTeamApi(
  payload: import('@shared/types/entities').CreateTeamPayload,
): Promise<import('@shared/types/entities').Team> {
  return api.post('/teams/organizer-registration', payload);
}

/** Checkout is a Direct Charge on the competition association's Stripe account. */
export async function createTeamCheckoutSessionApi(
  teamId: number,
  successUrl: string,
  cancelUrl: string,
): Promise<{ checkoutUrl: string }> {
  return api.post(`/teams/${teamId}/checkout-session`, { successUrl, cancelUrl });
}
